import { MutableRefObject, useMemo } from 'react'
import { GridColumnMenuProps, GridSortModel } from '@microservices/wiskey-react-table-component'
import { GridApiPro } from '@microservices/wiskey-react-table-component/dist/DataGrid/DataGridV7/models/gridApiPro'
import { ColumnMenuPropsOverrides, GridColumnResizeParams } from '@mui/x-data-grid'

import { getSortParam } from '@pages/ConfiguredEntity/helpers'

import { SortType, useRestrictions } from '@hooks'
import { createRestrictions } from '@helpers'
import { MAX_VIEW_COLUMN_WIDTH, MIN_VIEW_COLUMN_WIDTH } from '@constants'
import {
  ApiRestrictionData,
  COLUMN_TYPE,
  RESTRICTION_TYPE,
  RestrictionDTO,
  TimeLineDTOType,
  TimeLineGridColumn,
} from '@types'

type UseTimeLineRestrictions = {
  config: TimeLineDTOType
  apiRef: MutableRefObject<GridApiPro>
  objectXInitialRestrictions: ApiRestrictionData
  objectYInitialRestrictions: ApiRestrictionData
}

export const useTimeLineRestrictions = (props: UseTimeLineRestrictions) => {
  const { config, apiRef, objectXInitialRestrictions, objectYInitialRestrictions } = props

  const {
    restrictions: objectYRestrictions,
    isLoadingRestrictions: isLoadingObjectYRestrictions,
    updateRestrictions: updateObjectYRestrictions,
    removeAllRestrictions: removeAllObjectYRestrictions,
    removeRestrictions,
  } = useRestrictions(config.objectY.entityId, undefined, objectYInitialRestrictions)

  const {
    restrictions: objectXRestrictions,
    isLoadingRestrictions: isLoadingObjectXRestrictions,
    removeAllRestrictions: removeAllObjectXRestrictions,
    updateRestrictions: updateObjectXRestrictions,
  } = useRestrictions(config.objectX.entityId, undefined, objectXInitialRestrictions)

  const sortModel: SortType[] = useMemo(() => {
    return [...objectYRestrictions.sort]
      .sort((a, b) => Number(a.value?.priority) - Number(b.value?.priority))
      .map(sortItem => ({
        field: sortItem.fieldCode,
        sortKey: sortItem.value.columnToSort,
        sort: sortItem.value.sortDirection,
      }))
  }, [objectYRestrictions.sort])

  const handleResetRestrictions = (params: GridColumnMenuProps & ColumnMenuPropsOverrides) => {
    const colDef: TimeLineGridColumn = params.colDef

    if (colDef.columnType === COLUMN_TYPE.OBJECT_X) {
      removeAllObjectXRestrictions()

      return
    }

    removeAllObjectYRestrictions()
  }

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    if (!params) {
      return
    }

    const colDef: TimeLineGridColumn = params.colDef

    const restrictionsProps = {
      fieldName: colDef.headerName as string,
      fieldCode: colDef.field,
      viewId:
        colDef.columnType === COLUMN_TYPE.OBJECT_X
          ? (config.objectX.entityId as string)
          : (config.objectY.entityId as string),
      restrictionType: RESTRICTION_TYPE.WIDTH,
      value: Math.min(
        MAX_VIEW_COLUMN_WIDTH,
        Math.max(MIN_VIEW_COLUMN_WIDTH, Math.ceil(params.width as number))
      ),
    }

    if (colDef.columnType === COLUMN_TYPE.OBJECT_X) {
      return updateObjectXRestrictions([restrictionsProps])
    }

    updateObjectYRestrictions([restrictionsProps])
  }

  const handleChangeModelSort = (value: GridSortModel | null) => {
    if (value === null) {
      removeRestrictions(RESTRICTION_TYPE.SORT)
    }

    if (!value?.length) {
      return
    }

    const sort: SortType[] = getSortParam(value, apiRef)

    const newRestrictions: RestrictionDTO[] = sort.map((currentSorting, currentSortingOrder) =>
      createRestrictions({
        fieldName: currentSorting.field,
        fieldCode: currentSorting.field,
        viewId: config.objectY.entityId as string,
        restrictionType: RESTRICTION_TYPE.SORT,
        sortDirection: currentSorting.sort,
        columnToSort: currentSorting.sortKey,
        priority: currentSortingOrder,
      })
    )

    updateObjectYRestrictions(newRestrictions, undefined, true)
  }

  return {
    state: {
      sortModel,
      objectYRestrictions,
      objectXRestrictions,
      isLoadingObjectYRestrictions,
      isLoadingObjectXRestrictions,
    },
    data: {},
    handlers: {
      handleColumnWidthChange,
      handleChangeModelSort,
      handleResetRestrictions,
      updateObjectYRestrictions,
      updateObjectXRestrictions,
    },
  }
}
